/*
  Storefront essentials — small, dependency-free enhancements.
  Everything here is progressive: the pages work without it.
  1. Collection "Load more": appends the next page in place (real link underneath).
  2. Product page: no pre-selected size; Add to cart reads "Select a size" until chosen.
  3. "Goes with" row: loads complementary products via Section Rendering; one-tap add.
  4. Cart "Customers also add": same source, excludes what is in the cart; chips add in one tap.
  5. Cart page: stepper and Remove post to /cart/change.js and the page re-renders itself.
  6. Full-screen gallery (theme's Magnific popup) gets swipe on phones.
*/
(function () {
  'use strict';

  /* ---------- 1. Load more ---------- */
  function initLoadMore() {
    var btn = document.querySelector('[data-load-more]');
    var grid = document.querySelector('[data-collection-grid]');
    if (!btn || !grid) return;
    var sectionId = btn.getAttribute('data-section-id');

    var slot = btn.closest('[data-collection-more]') || btn.parentElement;
    var loading = false;
    function loadNext() {
      var url = btn.getAttribute('href');
      if (!url || loading) return;
      loading = true;
      btn.classList.add('is-loading');
      slot.classList.add('is-loading');
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      fetch(url + sep + 'sections=collection-template', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (json) {
          var html = json['collection-template'];
          if (!html) { window.location.href = url; return; }
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = doc.querySelectorAll('[data-collection-grid] > .grid-product');
          items.forEach(function (item) { grid.appendChild(item); });
          var nextBtn = doc.querySelector('[data-load-more]');
          var count = doc.querySelector('[data-collection-shown]');
          var shown = document.querySelector('[data-collection-shown]');
          if (count && shown) {
            var total = shown.getAttribute('data-total');
            var visible = grid.querySelectorAll('.grid-product').length;
            shown.textContent = visible + ' of ' + total;
          }
          btn.classList.remove('is-loading');
          slot.classList.remove('is-loading');
          loading = false;
          if (nextBtn) {
            btn.setAttribute('href', nextBtn.getAttribute('href'));
          } else {
            // last page: the slot goes too, so nothing sits between the grid and the footer
            slot.remove();
          }
          if (window.lazySizes && window.lazySizes.autoSizer) window.lazySizes.autoSizer.checkElems();
        })
        .catch(function () { window.location.href = url; });
    }
    btn.addEventListener('click', function (e) { e.preventDefault(); loadNext(); });
    // Progressive loading: fetch the next page as the customer nears the end of the grid.
    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      btn.classList.add('is-auto');
      slot.classList.add('is-auto');
      var spinner = document.createElement('span');
      spinner.className = 'collection-more__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      slot.appendChild(spinner);
      // numbered page links are redundant while pages load on scroll; they stay in the DOM for crawlers and no-script visitors
      document.querySelectorAll('.collection-pagination').forEach(function (p) { p.classList.add('is-auto'); });
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) loadNext(); });
      }, { rootMargin: '900px 0px' });
      io.observe(btn);
    }
  }

  /* ---------- 1b. Category row: the bold current item is scrolled into view on load ---------- */
  function initCategoryRow() {
    var cur = document.querySelector('.collection__header .is-current, .collection__header [aria-current="page"]');
    if (!cur) return;
    // walk up to the element that actually scrolls sideways
    var el = cur.parentElement;
    while (el && el !== document.body) {
      var o = getComputedStyle(el).overflowX;
      if ((o === 'auto' || o === 'scroll') && el.scrollWidth > el.clientWidth + 1) break;
      el = el.parentElement;
    }
    if (!el || el === document.body) return;
    var offset = cur.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    var target = offset - (el.clientWidth - cur.offsetWidth) / 2;
    el.scrollLeft = target < 0 ? 0 : target;
  }

  /* ---------- 2. Require a size ---------- */
  function initSizeGuard() {
    var form = document.querySelector('form[data-product-form]');
    if (!form) return;
    var sizeField = null;
    form.querySelectorAll('.single-option-radio, select[data-option-name]').forEach(function (fs) {
      if (fs.getAttribute('data-option-name') === 'size') sizeField = fs;
    });
    if (!sizeField) return;
    var isSelect = sizeField.tagName === 'SELECT';
    var button = form.querySelector('.btn--add-to-cart');
    var text = button ? button.querySelector('.btn__text') : null;
    var payment = form.querySelector('.shopify-payment-button');
    var selectEl = form.querySelector('#ProductSelect');
    var placeholder = form.getAttribute('data-select-size-text') || 'Select a size';
    var addText = (window.theme && window.theme.strings && window.theme.strings.addToCart) || 'Add to cart';
    var item = sizeField.closest('.product-form__item') || sizeField;
    var hint = null;

    function sizeChosen() {
      return isSelect ? !!sizeField.value : !!sizeField.querySelector('input:checked');
    }
    function showHint() {
      if (!hint) {
        hint = document.createElement('p');
        hint.className = 'product-form__hint';
        hint.setAttribute('role', 'alert');
        hint.textContent = placeholder;
        item.appendChild(hint);
      }
      hint.hidden = false;
      sizeField.classList.add('is-missing');
      var header = document.querySelector('.header-wrapper');
      var offset = (header ? header.getBoundingClientRect().height : 0) + 24;
      var y = item.getBoundingClientRect().top + window.pageYOffset - offset;
      if (item.getBoundingClientRect().top < offset) window.scrollTo({ top: y, behavior: 'smooth' });
    }
    function apply() {
      if (!button) return;
      if (!sizeChosen()) {
        // theme.js marks "no variant" as Unavailable; keep the real button so the customer can act
        button.removeAttribute('disabled');
        button.classList.remove('disabled');
        if (text) text.textContent = addText;
        if (payment) payment.style.display = 'none';
        if (selectEl) selectEl.selectedIndex = -1;
      } else {
        if (payment) payment.style.display = '';
        if (hint) hint.hidden = true;
        sizeField.classList.remove('is-missing');
      }
    }
    apply();
    setTimeout(apply, 50);
    sizeField.addEventListener('change', function () {
      // theme.js updates the button on variantChange; re-check after it runs
      setTimeout(apply, 0);
    });
    form.addEventListener('submit', function (e) {
      if (!sizeChosen()) { e.preventDefault(); apply(); showHint(); }
    });
  }

  /* ---------- 2b. Sizes that are sold out for the chosen colour look sold out before you tap ---------- */
  function initAvailability() {
    var form = document.querySelector('form[data-product-form]');
    var jsonEl = document.querySelector('script[id^="ProductJson-"]');
    if (!form || !jsonEl) return;
    var product;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    var fields = {};
    form.querySelectorAll('.single-option-radio[data-option-name], select[data-option-name]').forEach(function (f) { fields[f.getAttribute('data-option-name')] = f; });
    var sizeField = fields.size;
    if (!sizeField || !product.variants) return;
    var sizeIndex = -1, otherIndexes = [];
    (product.options || []).forEach(function (name, i) {
      var h = String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-');
      if (h === 'size') sizeIndex = i; else otherIndexes.push({ i: i, field: fields[h] });
    });
    if (sizeIndex === -1) return;
    function currentOther(i, field) {
      if (!field) return null;
      if (field.tagName === 'SELECT') return field.value || null;
      var on = field.querySelector('input:checked');
      return on ? on.value : null;
    }
    function update() {
      var chosen = otherIndexes.map(function (o) { return { i: o.i, v: currentOther(o.i, o.field) }; });
      var available = {};
      product.variants.forEach(function (v) {
        var ok = chosen.every(function (c) { return c.v === null || v.options[c.i] === c.v; });
        if (ok && v.available) available[v.options[sizeIndex]] = true;
      });
      if (sizeField.tagName === 'SELECT') {
        Array.prototype.forEach.call(sizeField.options, function (o) {
          if (!o.value) return;
          var base = o.getAttribute('data-base') || o.textContent.replace(/ \u2013 sold out$/i, '');
          o.setAttribute('data-base', base);
          o.textContent = available[o.value] ? base : base + ' \u2013 sold out';
        });
      } else {
        sizeField.querySelectorAll('input[type="radio"]').forEach(function (input) {
          var label = sizeField.querySelector('label[for="' + input.id + '"]');
          if (!label) return;
          var soldOut = !available[input.value];
          label.classList.toggle('disabled', soldOut);
          label.classList.toggle('is-soldout', soldOut);
          if (soldOut) label.setAttribute('title', input.value + ' sold out'); else label.removeAttribute('title');
        });
      }
    }
    otherIndexes.forEach(function (o) { if (o.field) o.field.addEventListener('change', function () { setTimeout(update, 0); }); });
    update();
  }

  /* ---------- 3 + 4. Complementary rows ---------- */
  function money(cents) {
    var fmt = (window.theme && window.theme.strings && window.theme.strings.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount).replace(/<[^>]+>/g, '');
  }

  /* Suggestion pool = Shopify complementary (Search & Discovery pairings) + Shopify related + the fallback
     collection (Best Sellers; Skate when skate gear is in the cart). Merged, deduped, never the current product
     or anything already in the cart, only items that can be added right now.
     Rank: 1) kind for the cart — apparel: hats, then apparel, then stickers, skate last; skate: skate hardware,
     then hats, then apparel; 2) complementary before related before best sellers; 3) when the cart knows a
     free-shipping gap, an item whose price closes it; 4) cheaper first. */
  function fetchGoesWith(box, max, exclude) {
    var productId = box.getAttribute('data-product-id');
    var base = box.getAttribute('data-base-url') || '/recommendations/products';
    var sectionId = box.getAttribute('data-section-id') || 'product-goes-with';
    var fallbackUrl = box.getAttribute('data-fallback-url');
    var kind = box.getAttribute('data-kind') || 'apparel';
    var gap = parseInt(box.getAttribute('data-gap') || '0', 10) || 0;
    var headings = { apparel: 'Worn with', skate: 'Set up with' };
    var seen = {};
    exclude.forEach(function (id) { seen[String(id)] = true; });
    function get(url) {
      return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.text() : ''; }).catch(function () { return ''; });
    }
    function parse(html, source) {
      if (!html) return [];
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var inner = doc.querySelector('[data-goes-with-inner]');
      if (!inner) return [];
      if (inner.getAttribute('data-heading-apparel')) headings = { apparel: inner.getAttribute('data-heading-apparel'), skate: inner.getAttribute('data-heading-skate') || headings.skate };
      return Array.prototype.slice.call(inner.querySelectorAll('.goes-with__item')).filter(function (el) {
        var id = String(el.getAttribute('data-product-id'));
        if (seen[id]) return false;
        if (!el.querySelector('[data-add-variant], [data-pick]')) return false;
        seen[id] = true;
        el.setAttribute('data-source', source);
        return true;
      });
    }
    var tiers = kind === 'skate' ? { skate: 0, hat: 1, apparel: 2, sticker: 3 } : { hat: 0, apparel: 1, sticker: 2, skate: 3 };
    var sourceRank = { complementary: 0, related: 1, fallback: 2 };
    function score(el) {
      var tier = tiers[el.getAttribute('data-kind') || 'apparel']; if (tier === undefined) tier = 2;
      var price = parseInt(el.getAttribute('data-price') || '0', 10) || 0;
      var closesGap = gap > 0 && price >= gap ? 0 : 1;
      return [tier, sourceRank[el.getAttribute('data-source')] || 0, closesGap, price];
    }
    var reqs = [];
    if (productId) {
      reqs.push(get(base + '?section_id=' + sectionId + '&product_id=' + productId + '&intent=complementary&limit=6'));
      reqs.push(get(base + '?section_id=' + sectionId + '&product_id=' + productId + '&intent=related&limit=6'));
    } else { reqs.push(Promise.resolve('')); reqs.push(Promise.resolve('')); }
    reqs.push(fallbackUrl ? get(fallbackUrl + '?section_id=goes-with-fallback') : Promise.resolve(''));
    return Promise.all(reqs).then(function (htmls) {
      // parse in source order so a product seen in two sources keeps its strongest source
      var pool = parse(htmls[0], 'complementary').concat(parse(htmls[1], 'related'), parse(htmls[2], 'fallback'));
      pool.sort(function (x, y) { var sx = score(x), sy = score(y); for (var i = 0; i < sx.length; i++) { if (sx[i] !== sy[i]) return sx[i] - sy[i]; } return 0; });
      if (!pool.length) return null;
      return { items: pool.slice(0, max), heading: headings[kind] };
    });
  }

  function render(box, result, cartMode) {
    if (!result) { box.remove(); return; }
    if (cartMode) {
      var wrap = box.querySelector('[data-cart-add-one-items]');
      result.items.forEach(function (el) { wrap.appendChild(el); });
      // The template leads with "Add $X more and US shipping is free" + "Any of these gets you there."
      // If a suggestion does not actually close the gap, fall back to the plain heading so the promise stays true.
      var gapCents = parseInt(box.getAttribute('data-gap') || '0', 10) || 0;
      var closes = gapCents > 0 && result.items.every(function (el) { return (parseInt(el.getAttribute('data-price') || '0', 10) || 0) >= gapCents; });
      var title = box.querySelector('.rb-cart__also-title'); var lead = box.querySelector('.rb-cart__also-lead');
      if (gapCents > 0 && !closes) { if (title) { title.textContent = 'People also add'; title.classList.remove('rb-cart__also-title--gap'); } if (lead) lead.textContent = 'Tap a size or color to add one.'; }
    } else {
      var aside = document.createElement('aside');
      aside.className = 'goes-with';
      aside.setAttribute('aria-label', result.heading);
      var h = document.createElement('h2'); h.className = 'goes-with__title h4'; h.textContent = result.heading; aside.appendChild(h);
      var grid = document.createElement('div'); grid.className = 'goes-with__grid';
      result.items.forEach(function (el) { grid.appendChild(el); });
      aside.appendChild(grid);
      box.innerHTML = ''; box.appendChild(aside);
    }
    box.hidden = false;
    bindAdds(box);
  }

  function initGoesWith() {
    var box = document.querySelector('[data-goes-with]');
    if (!box || !box.getAttribute('data-product-id')) return;
    var max = 2;
    fetchGoesWith(box, max, [box.getAttribute('data-product-id')]).then(function (r) { render(box, r, false); }).catch(function () { box.remove(); });
  }

  function initCartAddOne() {
    var box = document.querySelector('[data-cart-add-one]');
    if (!box) return;
    if (!box.getAttribute('data-product-id')) { box.remove(); return; }
    var inCart = (box.getAttribute('data-in-cart') || '').split(',');
    var max = parseInt(box.getAttribute('data-max') || '1', 10) || 1;
    fetchGoesWith(box, max, inCart).then(function (r) { render(box, r, true); }).catch(function () { box.remove(); });
  }

  /* one quiet instruction per add-on block (not one per item) */
  function ensureBlockCaption(el) {
    var box = el.closest('[data-one-tap]');
    if (!box || box.querySelector('.goes-with__caption--block')) return;
    if (box.querySelector('.rb-cart__also-lead')) return; // the cart's lead line already says how to add
    var title = box.querySelector('.rb-cart__also-title, .goes-with__title');
    var cap = document.createElement('p'); cap.className = 'goes-with__caption goes-with__caption--block';
    cap.textContent = 'Tap a size or color to add it to your cart.';
    if (title && title.parentNode) title.parentNode.insertBefore(cap, title.nextSibling); else box.insertBefore(cap, box.firstChild);
  }

  /* inline variant choice for multi-variant pairings: chips for a few, a select for many */
  function buildPickers(scope) {
    scope.querySelectorAll('[data-pick]').forEach(function (pick) {
      if (pick.getAttribute('data-built')) return;
      pick.setAttribute('data-built', '1');
      var variants = [];
      try { variants = JSON.parse(pick.getAttribute('data-variants') || '[]'); } catch (e) { return; }
      variants = variants.filter(function (v) { return v.available; });
      var btn = pick.parentNode.querySelector('[data-add-variant]');
      if (!variants.length || !btn) { pick.remove(); return; }
      var optionNames = (pick.getAttribute('data-options') || '').split('|');
      var label = optionNames.length === 1 ? optionNames[0] : 'Option';
      var oneTap = !!pick.closest('[data-one-tap]');
      function choose(id) { btn.setAttribute('data-add-variant', String(id)); btn.disabled = false; }
      /* one-tap with colour + size (any 2–3 options): a chip row per option; the last option (size) adds on tap */
      if (oneTap && optionNames.length >= 2 && variants.length <= 24) {
        btn.remove();
        var sizeIdx = optionNames.length - 1;
        optionNames.forEach(function (n, i) { if (String(n).toLowerCase() === 'size') sizeIdx = i; });
        var chosen = {};
        var valuesFor = function (i) { var out = []; variants.forEach(function (v) { if (out.indexOf(v.options[i]) === -1) out.push(v.options[i]); }); return out; };
        var matches = function (v, skipIdx) { return Object.keys(chosen).every(function (k) { return parseInt(k, 10) === skipIdx || v.options[k] === chosen[k]; }); };
        var sizeRow = document.createElement('div'); sizeRow.className = 'goes-with__chips'; sizeRow.setAttribute('role', 'group'); sizeRow.setAttribute('aria-label', 'Add ' + optionNames[sizeIdx].toLowerCase());
        ensureBlockCaption(pick);
        function renderSizes() {
          sizeRow.innerHTML = '';
          valuesFor(sizeIdx).forEach(function (val) {
            var v = null; variants.forEach(function (x) { if (!v && x.options[sizeIdx] === val && matches(x, sizeIdx)) v = x; });
            var c = document.createElement('button'); c.type = 'button'; c.className = 'goes-with__chip'; c.textContent = '+ ' + val;
            if (!v) { c.disabled = true; c.setAttribute('aria-label', val + ' sold out'); }
            else { c.setAttribute('aria-label', 'Add ' + val); c.addEventListener('click', function () { sizeRow.querySelectorAll('.goes-with__chip').forEach(function (x) { x.classList.remove('is-on'); }); c.classList.add('is-on'); addToCart(v.id, c, sizeRow); }); }
            sizeRow.appendChild(c);
          });
        }
        optionNames.forEach(function (name, i) {
          if (i === sizeIdx) return;
          var vals = valuesFor(i);
          chosen[i] = vals[0];
          var row = document.createElement('div'); row.className = 'goes-with__chips goes-with__chips--opt'; row.setAttribute('role', 'radiogroup'); row.setAttribute('aria-label', name);
          var lab = document.createElement('span'); lab.className = 'goes-with__optlabel'; lab.textContent = name; row.appendChild(lab);
          vals.forEach(function (val, vi) {
            var c = document.createElement('button'); c.type = 'button'; c.className = 'goes-with__chip goes-with__chip--opt' + (vi === 0 ? ' is-on' : ''); c.textContent = val;
            c.setAttribute('role', 'radio'); c.setAttribute('aria-checked', vi === 0 ? 'true' : 'false');
            c.addEventListener('click', function () {
              chosen[i] = val;
              row.querySelectorAll('.goes-with__chip').forEach(function (x) { x.classList.remove('is-on'); x.setAttribute('aria-checked', 'false'); });
              c.classList.add('is-on'); c.setAttribute('aria-checked', 'true');
              renderSizes();
            });
            row.appendChild(c);
          });
          pick.appendChild(row);
        });
        pick.appendChild(sizeRow);
        renderSizes();
        return;
      }
      if (variants.length <= 8) {
        var row = document.createElement('div'); row.className = 'goes-with__chips';
        row.setAttribute('role', oneTap ? 'group' : 'radiogroup'); row.setAttribute('aria-label', oneTap ? 'Add ' + label.toLowerCase() : label);
        if (oneTap) {
          btn.remove();
          ensureBlockCaption(pick);
        }
        variants.forEach(function (v) {
          var c = document.createElement('button'); c.type = 'button'; c.className = 'goes-with__chip';
          c.textContent = oneTap ? '+ ' + v.title : v.title;
          if (oneTap) {
            c.setAttribute('aria-label', 'Add ' + v.title);
            c.addEventListener('click', function () {
              row.querySelectorAll('.goes-with__chip').forEach(function (x) { x.classList.remove('is-on'); });
              c.classList.add('is-on');
              addToCart(v.id, c, row);
            });
          } else {
            c.setAttribute('role', 'radio'); c.setAttribute('aria-checked', 'false');
            c.addEventListener('click', function () {
              row.querySelectorAll('.goes-with__chip').forEach(function (x) { x.classList.remove('is-on'); x.setAttribute('aria-checked', 'false'); });
              c.classList.add('is-on'); c.setAttribute('aria-checked', 'true'); choose(v.id);
            });
          }
          row.appendChild(c);
        });
        pick.appendChild(row);
      } else {
        var sel = document.createElement('select'); sel.className = 'goes-with__select'; sel.setAttribute('aria-label', oneTap ? 'Add ' + label.toLowerCase() : label);
        var ph = document.createElement('option'); ph.value = ''; ph.textContent = (oneTap ? 'Pick ' : 'Choose ') + label.toLowerCase() + (oneTap ? ' to add' : ''); ph.disabled = true; ph.selected = true; sel.appendChild(ph);
        variants.forEach(function (v) { var o = document.createElement('option'); o.value = v.id; o.textContent = v.title; sel.appendChild(o); });
        if (oneTap) {
          btn.remove();
          sel.addEventListener('change', function () {
            if (!sel.value) return;
            sel.disabled = true;
            var fake = { classList: sel.classList, getAttribute: function () { return null; }, set textContent(t) { ph.textContent = t; ph.selected = true; sel.value = ''; ph.disabled = false; }, set disabled(v) { sel.disabled = v; } };
            addToCart(sel.value, fake, null);
          });
        } else {
          sel.addEventListener('change', function () { if (sel.value) choose(sel.value); });
        }
        pick.appendChild(sel);
      }
    });
  }

  function updateBadge() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
      document.querySelectorAll('[data-cart-count]').forEach(function (bubble) {
        if (cart.item_count > 0) { bubble.classList.add('cart-link__bubble--visible'); bubble.textContent = cart.item_count; }
        else { bubble.classList.remove('cart-link__bubble--visible'); bubble.textContent = ''; }
      });
      return cart;
    });
  }

  var cartRefresh = null; // set by initCart on the cart page

  function addToCart(id, el, group) {
    if (!id) return Promise.resolve();
    if (group) group.querySelectorAll('button').forEach(function (b) { b.disabled = true; }); else el.disabled = true;
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () {
        el.classList.add('is-added');
        el.textContent = el.getAttribute('data-added-text') || 'Added';
        if (cartRefresh) {
          var card = el.closest('.goes-with__item');
          var nameEl = card ? card.querySelector('.goes-with__name') : null;
          cartRefresh({ id: String(id), title: nameEl ? nameEl.textContent.trim() : '', detail: el.tagName === 'BUTTON' && el.classList.contains('goes-with__chip') ? el.textContent.replace(/^Added$/, '').trim() : '' });
          return;
        }
        updateBadge();
      })
      .catch(function () {
        if (group) group.querySelectorAll('button').forEach(function (b) { b.disabled = false; }); else el.disabled = false;
      });
  }

  function bindAdds(scope) {
    buildPickers(scope);
    scope.querySelectorAll('[data-add-variant]').forEach(function (btn) {
      if (btn.closest('[data-one-tap]') && !btn.classList.contains('goes-with__add--chip')) { btn.classList.add('goes-with__add--chip'); btn.textContent = '+ Add'; ensureBlockCaption(btn); }
      btn.addEventListener('click', function () { addToCart(btn.getAttribute('data-add-variant'), btn, null); });
    });
  }

  /* ---------- 5. Cart page: stepper + Remove update in place; the page re-renders itself ---------- */
  function initCart() {
    var root = document.querySelector('[data-cart-root]');
    if (!root) return;
    var busy = false;

    function confirmAdded(added) {
      if (!added || !added.id) return;
      var line = root.querySelector('.rb-cart__line[data-variant-id="' + added.id + '"]');
      if (line) {
        line.classList.add('is-new');
        var name = line.querySelector('.rb-cart__name');
        var tag = document.createElement('span'); tag.className = 'rb-cart__just-added'; tag.textContent = 'Just added';
        if (name && name.parentNode) name.parentNode.insertBefore(tag, name.nextSibling);
        setTimeout(function () { line.classList.remove('is-new'); tag.remove(); }, 4000);
        var rect = line.getBoundingClientRect();
        var header = document.querySelector('.header-wrapper');
        var top = header ? header.getBoundingClientRect().height : 0;
        if (rect.top < top || rect.bottom > window.innerHeight) line.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      var box = root.querySelector('.rb-cart__also') || root.querySelector('.rb-cart__lines');
      if (!box) return;
      var status = document.createElement('p');
      status.className = 'rb-cart__status';
      status.setAttribute('role', 'status');
      status.textContent = 'Added to your cart' + (added.title ? ': ' + added.title : '');
      box.insertBefore(status, box.firstChild);
      setTimeout(function () { status.classList.add('is-leaving'); setTimeout(function () { status.remove(); }, 400); }, 3500);
    }

    function refresh(added) {
      return fetch(window.location.pathname + window.location.search, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-cart-root]');
          if (!fresh) { window.location.reload(); return; }
          root.replaceWith(fresh);
          root = fresh;
          busy = false;
          bind();
          initCartAddOne();
          updateBadge();
          confirmAdded(added);
        })
        .catch(function () { window.location.reload(); });
    }
    cartRefresh = refresh;

    function change(line, qty) {
      if (busy) return;
      busy = true;
      root.classList.add('is-busy');
      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty })
      })
        .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
        .then(refresh)
        .catch(function () { window.location.reload(); });
    }

    function bind() {
      root.querySelectorAll('[data-empty-picks]').forEach(function (box) { bindAdds(box); });
      root.querySelectorAll('[data-cart-qty]').forEach(function (q) {
        var input = q.querySelector('input');
        var line = parseInt(input.getAttribute('data-line'), 10);
        var minus = q.querySelector('[data-qty-minus]');
        // at 1 the minus is off: removing is a deliberate tap on Remove, not a slip on −
        minus.disabled = (parseInt(input.value, 10) || 1) <= 1;
        minus.addEventListener('click', function () { change(line, Math.max(1, (parseInt(input.value, 10) || 1) - 1)); });
        q.querySelector('[data-qty-plus]').addEventListener('click', function () { change(line, (parseInt(input.value, 10) || 0) + 1); });
        input.addEventListener('change', function () { change(line, Math.max(0, parseInt(input.value, 10) || 0)); });
      });
      root.querySelectorAll('[data-cart-remove]').forEach(function (a) {
        a.addEventListener('click', function (e) { e.preventDefault(); change(parseInt(a.getAttribute('data-cart-remove'), 10), 0); });
      });
    }
    bind();
  }

  /* ---------- 6. Full-screen gallery on phones: swipe moves between images (Magnific has arrows only) ---------- */
  function initGallerySwipe() {
    if (!document.querySelector('.product-single__photos')) return;
    var x0 = null, y0 = null;
    document.addEventListener('touchstart', function (e) {
      if (!e.target.closest || !e.target.closest('.mfp-container')) { x0 = null; return; }
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
    }, { passive: true });
    document.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
      x0 = null;
      var mp = window.jQuery && window.jQuery.magnificPopup && window.jQuery.magnificPopup.instance;
      if (!mp || !mp.isOpen || !mp.items || mp.items.length < 2) return;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) { if (dx < 0) mp.next(); else mp.prev(); }
    }, { passive: true });
  }

  /* ---------- colour name next to the Color label ---------- */
  function initColorName() {
    var name = document.querySelector('[data-color-name]');
    var field = document.querySelector('.single-option-radio[data-option-name="color"], .single-option-radio[data-option-name="colour"]');
    if (!name || !field) return;
    field.addEventListener('change', function (e) { if (e.target && e.target.value) name.textContent = e.target.value; });
  }

  /* quantity stepper */
  function initQty() {
    var box = document.querySelector('[data-qty]');
    if (!box) return;
    var input = box.querySelector('input');
    box.querySelector('[data-qty-minus]').addEventListener('click', function () { input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1); });
    box.querySelector('[data-qty-plus]').addEventListener('click', function () { input.value = (parseInt(input.value, 10) || 1) + 1; });
  }

  /* size chart: lift Printful's table out of the description into the #size-chart block */
  function initSizeChart() {
    var chart = document.querySelector('[data-size-chart][data-from-description]');
    var desc = document.querySelector('.product-single__description');
    if (!chart || !desc) return;
    var table = desc.querySelector('table');
    if (!table) { if (!chart.querySelector('table')) chart.hidden = true; return; }
    // Printful markup: <p><strong class="size-guide-title">Size guide</strong></p><div class="table-responsive"><table>…</table></div>
    desc.querySelectorAll('.size-guide-title').forEach(function (el) { var p = el.closest('p') || el; p.remove(); });
    Array.prototype.slice.call(desc.querySelectorAll('p, h2, h3, h4, strong')).forEach(function (el) {
      var t = (el.textContent || '').replace(/ /g, ' ').trim().toLowerCase();
      if (t === 'size guide' || t === 'size guide:' || t === 'size chart') { (el.closest('p') || el).remove(); }
    });
    var wrapper = table.parentElement;
    table.querySelectorAll('[style], [width]').forEach(function (el) { el.removeAttribute('style'); el.removeAttribute('width'); });
    table.querySelectorAll('colgroup').forEach(function (cg) { cg.remove(); });
    table.removeAttribute('style'); table.removeAttribute('width');
    table.querySelectorAll('td, th').forEach(function (c) { var t = c.textContent.replace(/\s*\((?:inches|inch|in|cm)\)\s*/ig, '').replace(/\u00a0/g, ' ').trim(); c.textContent = t; });
    (chart.querySelector('.size-chart__body') || chart).appendChild(table);
    if (wrapper && wrapper !== desc && !(wrapper.textContent || '').trim() && !wrapper.querySelector('img')) wrapper.remove();
    chart.hidden = false;
    Array.prototype.slice.call(desc.querySelectorAll('p')).forEach(function (p) { if (!(p.textContent || '').replace(/ /g, '').trim() && !p.querySelector('img')) p.remove(); });
  }

  /* size guide link: scroll so the chart clears the fixed header; tidy chart labels */
  function initSizeGuideLink() {
    var link = document.querySelector('.product-form__size-guide');
    var chart = document.querySelector('#size-chart');
    if (!link) return;
    if (!chart || chart.hidden) { link.remove(); return; }
    chart.querySelectorAll('td, th').forEach(function (c) { c.textContent = c.textContent.replace(/\s*\((inches|in|cm)\)\s*/i, '').trim(); });
    link.addEventListener('click', function (e) {
      e.preventDefault();
      if ('open' in chart) chart.open = true;
      var header = document.querySelector('.header-wrapper');
      var offset = (header ? header.getBoundingClientRect().height : 0) + 16;
      var y = chart.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    });
  }

  /* PageFly legacy pages (2020–21 runtime): PageFly retired cdn.pagefly.io/pagefly/2.x/core/helper.js in 2026
     (it now returns HTML), so its lazy images never load and its accordions never open. This swaps the
     data-src/data-srcset placeholders in and wires the accordion headers. Remove with projects/pagefly-exit. */
  function initPageflyLegacy() {
    var lazy = document.querySelectorAll('.pf-img-lazy, [data-pf-type] source[data-srcset], [data-pf-type] img[data-srcset], [data-pf-type] img[data-src]');
    Array.prototype.slice.call(lazy).forEach(function (el) {
      var srcset = el.getAttribute('data-srcset');
      var src = el.getAttribute('data-src');
      if (srcset) el.setAttribute('srcset', srcset);
      if (src) el.setAttribute('src', src);
      else if (srcset && el.tagName === 'IMG' && (!el.getAttribute('src') || /^data:/.test(el.getAttribute('src')))) {
        el.setAttribute('src', srcset.split(',')[0].trim().split(/\s+/)[0]);
      }
      if (el.tagName === 'IMG' && !el.getAttribute('loading')) el.setAttribute('loading', 'lazy');
      el.classList.remove('pf-img-lazy');
    });
    Array.prototype.slice.call(document.querySelectorAll('[data-action="accordion2"], .accordion__header')).forEach(function (btn) {
      var panel = btn.nextElementSibling;
      if (!panel || !panel.classList.contains('accordion__content')) return;
      btn.setAttribute('aria-expanded', panel.style.display !== 'none' ? 'true' : 'false');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var open = panel.style.display === 'none';
        panel.style.display = open ? '' : 'none';
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.setAttribute('data-active', open ? 'true' : 'false');
        var icon = btn.querySelector('.fa');
        if (icon) { icon.classList.toggle('fa-plus', !open); icon.classList.toggle('fa-minus', open); }
      });
    });
  }

  /* Videos marked data-autoplay-inview (homepage banner): nothing downloads until the video is on screen.
     Desktop without reduced-motion or data-saver: play muted while visible, pause when scrolled away.
     Phones, reduced motion or data saver: poster + controls, plays on tap. */
  function initInViewVideo() {
    var videos = document.querySelectorAll('video[data-autoplay-inview]');
    if (!videos.length) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var saveData = navigator.connection && navigator.connection.saveData;
    var small = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    if (reduce || saveData || small || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          v.muted = true;
          if (v.preload === 'none') v.preload = 'auto';
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { threshold: [0, 0.5] });
    Array.prototype.slice.call(videos).forEach(function (v) { io.observe(v); });
  }

  function init() {
    initPageflyLegacy();
    initInViewVideo();
    initSizeChart();
    initSizeGuideLink();
    initQty();
    initColorName();
    initLoadMore();
    initCategoryRow();
    initSizeGuard();
    initAvailability();
    initGoesWith();
    initCart();
    initCartAddOne();
    initGallerySwipe();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
